import { cn } from "@/lib/cn";

interface LogoProps {
  variant?: "icon" | "wordmark" | "stacked";
  color?: "primary" | "white" | "gold";
  className?: string;
}

/**
 * Costa Dorada logo (recreated as SVG so it stays crisp at any size).
 * The CD monogram is two mirrored Ionic-style spirals — a C that curls
 * inward on the left, a D that curls outward on the right — paired with
 * the brand wave underneath.
 *
 * Variants:
 *   icon     — just the CD monogram (no text)
 *   wordmark — monogram + COSTA DORADA inline (responsive)
 *   stacked  — monogram above, COSTA DORADA below (for hero blocks)
 */
export function Logo({ variant = "wordmark", color = "primary", className }: LogoProps) {
  const fill =
    color === "white" ? "#ffffff" :
    color === "gold"  ? "#c8a560" :
    "#1e4d6b";

  const monogram = (
    <svg viewBox="0 0 220 200" className="h-full w-auto" aria-hidden="true">
      <defs>
        <linearGradient id={`waveGrad-${color}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={fill} stopOpacity="0.85" />
          <stop offset="100%" stopColor={fill} />
        </linearGradient>
      </defs>

      {/* LEFT spiral — the "C" — curls inward (counter-clockwise) */}
      <g transform="translate(20, 20)">
        {/* Outer C body */}
        <path
          d="M 78 0
             C 35 0  0 35  0 78
             C 0 121  35 156  78 156
             L 78 130
             C 49 130  26 107  26 78
             C 26 49  49 26  78 26
             Z"
          fill={fill}
        />
        {/* Inner curl (the spiral tail at bottom) */}
        <path
          d="M 78 130
             C 60 130  46 116  46 98
             C 46 86  54 76  66 73
             C 70 72  74 74  75 78
             C 76 82  74 86  70 87
             C 64 89  60 93  60 98
             C 60 108  67 116  78 116
             Z"
          fill={fill}
        />
      </g>

      {/* RIGHT spiral — the "D" — mirror of the C */}
      <g transform="translate(122, 20)">
        <path
          d="M 0 0
             C 43 0  78 35  78 78
             C 78 121  43 156  0 156
             L 0 130
             C 29 130  52 107  52 78
             C 52 49  29 26  0 26
             Z"
          fill={fill}
        />
        <path
          d="M 0 130
             C 18 130  32 116  32 98
             C 32 86  24 76  12 73
             C 8 72  4 74  3 78
             C 2 82  4 86  8 87
             C 14 89  18 93  18 98
             C 18 108  11 116  0 116
             Z"
          fill={fill}
        />
      </g>

      {/* Decorative wave underneath */}
      <g transform="translate(30, 180)">
        <path
          d="M 0 6
             Q 20 -4  40 6
             T 80 6
             T 120 6
             T 160 6"
          stroke={`url(#waveGrad-${color})`}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M 10 12
             Q 28 6  46 12
             T 82 12
             T 118 12
             T 150 12"
          stroke={fill}
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          opacity="0.6"
        />
      </g>
    </svg>
  );

  if (variant === "icon") {
    return (
      <div className={cn("inline-block h-10", className)} aria-label="Costa Dorada">
        {monogram}
      </div>
    );
  }

  if (variant === "stacked") {
    return (
      <div className={cn("inline-flex flex-col items-center", className)} aria-label="Costa Dorada">
        <div className="h-20">{monogram}</div>
        <div
          className="font-serif tracking-[0.18em] mt-1"
          style={{ color: fill, fontSize: "1.2em", fontWeight: 600 }}
        >
          COSTA DORADA
        </div>
      </div>
    );
  }

  return (
    <div className={cn("inline-flex items-center gap-3", className)} aria-label="Costa Dorada">
      <div className="h-10 shrink-0">{monogram}</div>
      <div
        className="font-serif tracking-[0.15em]"
        style={{ color: fill, fontWeight: 600 }}
      >
        COSTA DORADA
      </div>
    </div>
  );
}
